import { anzeigeName, type DashboardCoaching } from '@/core/types'
import type { Member } from '@/core/data/members'
import type { ViewConfig } from '@/core/views/viewConfig'
import { descriptionFor } from '@/variants'
import { currentVariant } from '@/core/data/session'
import { formatDatum } from '@/core/lib/dates'
import { isCoachOf } from '@/core/lib/orgMembers'

function stageLabelFor(stage: DashboardCoaching['stage']): string | undefined {
  return descriptionFor(currentVariant())
    .columns.find((c) => c.id === stage)
    ?.dbName
}

export function coachingsForMember(
  coachings: readonly DashboardCoaching[],
  member: Pick<Member, 'userId'> | null,
): DashboardCoaching[] {
  if (!member) return []
  return coachings.filter((c) => isCoachOf(member, c.coachId, c.secondCoachId))
}

function ueProgress(c: DashboardCoaching): string | null {
  return c.ueGesamt != null ? `${c.ueStattgefunden}/${c.ueGesamt} UE` : null
}

function voucherValidity(c: DashboardCoaching): string | null {
  if (!c.gueltigkeitBeginn || !c.gueltigkeitEnde) return null
  return `${formatDatum(c.gueltigkeitBeginn)} – ${formatDatum(c.gueltigkeitEnde)}`
}

function coachingMeta(c: DashboardCoaching): string {
  return [stageLabelFor(c.stage) ?? c.stage, ueProgress(c), voucherValidity(c)]
    .filter((x): x is string => !!x)
    .join(' · ')
}

export const memberCoachingsViewConfig: ViewConfig<DashboardCoaching> = {
  rowKey: (c) => c.id,
  title: anzeigeName,
  properties: [{ id: 'meta', value: coachingMeta }],
  emptyText: 'Keine Coachings',
  cards: true,
}

export function coachingsSummary(coachings: readonly DashboardCoaching[]): string {
  const columns = descriptionFor(currentVariant()).columns
  const terminalStage = columns[columns.length - 1]?.id
  const abgeschlossen = coachings.filter((c) => c.stage === terminalStage).length
  const laufend = coachings.length - abgeschlossen
  const ueOffen = coachings.reduce(
    (sum, c) => sum + (c.ueGesamt != null ? Math.max(0, c.ueGesamt - c.ueStattgefunden) : 0),
    0,
  )
  return `${laufend} laufend · ${abgeschlossen} abgeschlossen · ${ueOffen} UE offen`
}
